import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
} from '@mui/icons-material';
import { employeeAPI, validationAPI } from '../services/api';
import {
  downloadMassiveInpasExport,
  InpasPresenceValidation,
} from '../utils/inpasExport';

const MonthlyScheduleExport: React.FC = () => {
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [ente, setEnte] = useState('');
  const [entiList, setEntiList] = useState<string[]>([]);
  const [loadingEnti, setLoadingEnti] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingInpas, setLoadingInpas] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - 5 + i);

  // Carica lista enti al mount
  useEffect(() => {
    const loadEnti = async () => {
      try {
        setLoadingEnti(true);
        const response = await employeeAPI.getEnti();
        // L'API restituisce {enti: [...]}, quindi accediamo alla proprietà enti
        const enti = Array.isArray(response) ? response : (response?.enti || []);
        setEntiList(enti);
      } catch (err) {
        console.error('Errore caricamento enti:', err);
        setEntiList([]); // In caso di errore, imposta array vuoto
      } finally {
        setLoadingEnti(false);
      }
    };
    loadEnti();
  }, []);

  const handleExport = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const monthStr = `${year}-${month.toString().padStart(2, '0')}`;

      // Costruisci i parametri della query
      const params = new URLSearchParams();
      params.append('month', monthStr);
      if (ente) params.append('ente', ente);

      // Chiama l'API per scaricare il file
      const response = await fetch(
        `/api/admin/reports/monthly-schedule-export?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Errore durante l\'export');
      }

      // Scarica il file ZIP
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presenze_${monthStr}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Errore durante l\'export');
    } finally {
      setLoading(false);
    }
  };

  const handleInpasExport = async () => {
    try {
      setLoadingInpas(true);
      setError('');
      setSuccess('');

      const response = await validationAPI.getRegularValidationsForExport(year, month, false);
      const validations: InpasPresenceValidation[] = response.validations || [];

      if (validations.length === 0) {
        setError('Nessun cartellino regolare trovato per il periodo selezionato');
        return;
      }

      await downloadMassiveInpasExport(validations, year, month);
      setSuccess(`Esportazione INPAS completata: ${validations.length} cartellini generati.`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'export INPAS');
    } finally {
      setLoadingInpas(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
        Export Presenze Mensili (Ore Lavorate)
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Questo export genera un file ZIP contenente:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
              <li>Un file Excel separato per ogni ente</li>
              <li>Le ore lavorate giornaliere per ogni dipendente</li>
              <li>Le ore visualizzate sono limitate al tetto massimo contrattuale previsto</li>
              <li>I weekend sono evidenziati in giallo</li>
            </Typography>
          </Alert>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              E disponibile anche l'export massivo INPAS dei soli cartellini regolari gia validati per anno e mese selezionati.
              Il filtro ente si applica all'export ore lavorate in ZIP, mentre l'export INPAS usa il perimetro amministrativo consentito.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Anno"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Mese"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
              >
                {monthNames.map((name, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Ente (opzionale)"
                value={ente}
                onChange={(e) => setEnte(e.target.value)}
                disabled={loadingEnti}
                helperText="Lascia vuoto per esportare tutti gli enti"
              >
                <MenuItem value="">
                  <em>Tutti gli enti</em>
                </MenuItem>
                {entiList.map((enteItem) => (
                  <MenuItem key={enteItem} value={enteItem}>
                    {enteItem}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleExport}
                  disabled={loading}
                  size="large"
                >
                  {loading ? 'Esportazione in corso...' : 'Scarica ZIP con Excel'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={loadingInpas ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleInpasExport}
                  disabled={loadingInpas}
                  size="large"
                >
                  {loadingInpas ? 'Esportazione INPAS...' : 'Esporta cartellini INPAS'}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MonthlyScheduleExport;
